import { useEffect, useState } from 'react';
import {
  Bell,
  Keyboard,
  Move3D,
  PencilRuler,
  Plane,
  Power,
  RotateCw,
  Search,
  Space,
  X,
} from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { cn } from '@/lib/utils';

interface ShortcutEntry {
  keys: string[];
  label: string;
  scope: 'global' | 'pilot' | 'manual' | 'waypoint' | 'misc';
  icon: React.ComponentType<{ className?: string }>;
}

const SHORTCUTS: ShortcutEntry[] = [
  { keys: ['⌘', 'K'], label: 'Open command palette', scope: 'global', icon: Search },
  { keys: ['?'], label: 'Toggle this help overlay', scope: 'global', icon: Keyboard },
  { keys: ['Esc'], label: 'Close modal / cancel draft', scope: 'global', icon: X },
  { keys: ['Ctrl', 'Shift', 'M'], label: 'Cycle map mode (Direct · Waypoint · Manual)', scope: 'pilot', icon: Move3D },
  { keys: ['Click'], label: 'GOTO in Direct mode · Add waypoint in Waypoint mode', scope: 'pilot', icon: PencilRuler },
  { keys: ['Shift', 'Click'], label: 'Append target to GOTO queue · Multi-select drone', scope: 'pilot', icon: PencilRuler },
  { keys: ['Alt', 'Click'], label: 'Delete waypoint at cursor', scope: 'pilot', icon: PencilRuler },
  { keys: ['Right-click'], label: 'Open context menu (GOTO / Loiter / Save WP / Copy)', scope: 'pilot', icon: PencilRuler },
  { keys: ['Space'], label: 'Hold to engage manual control deadman', scope: 'manual', icon: Space },
  { keys: ['W', 'A', 'S', 'D'], label: 'Pitch / roll body-frame velocity', scope: 'manual', icon: Plane },
  { keys: ['Q', 'E'], label: 'Yaw left / right', scope: 'manual', icon: RotateCw },
  { keys: ['R', 'F'], label: 'Climb / descend', scope: 'manual', icon: Plane },
  { keys: ['Arrow keys'], label: 'Mirrors WASD', scope: 'manual', icon: Plane },
  { keys: ['Alt', 'E'], label: 'Open Emergency Stop dialog', scope: 'global', icon: Power },
  { keys: ['Alt', 'N'], label: 'Open Notifications', scope: 'global', icon: Bell },
];

const SCOPE_LABEL: Record<ShortcutEntry['scope'], string> = {
  global: 'Global',
  pilot: 'Pilot · map',
  manual: 'Manual control',
  waypoint: 'Waypoint mode',
  misc: 'Misc',
};

export function KeyboardShortcutsOverlay() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      // Toggle on "?" — handle both shift+/ and direct ?.
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName;
      const editable = target && (tag === 'INPUT' || tag === 'TEXTAREA' || target.isContentEditable);
      if (editable) return;
      if (e.key === '?' || (e.shiftKey && e.key === '/')) {
        e.preventDefault();
        setOpen((v) => !v);
      } else if (e.key === 'Escape' && open) {
        setOpen(false);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  if (!open) return null;

  const grouped = (Object.keys(SCOPE_LABEL) as ShortcutEntry['scope'][])
    .map((s) => ({ scope: s, items: SHORTCUTS.filter((sc) => sc.scope === s) }))
    .filter((g) => g.items.length > 0);

  return (
    <>
      <div className="fixed inset-0 z-[55] bg-black/55 backdrop-blur-sm" onClick={() => setOpen(false)} />
      <div className="fixed left-1/2 top-1/2 z-[55] w-[min(720px,92vw)] -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-xl border border-border-default bg-surface-1 shadow-[var(--shadow-pop)]">
        <header className="flex items-center gap-2 border-b border-border-default px-4 py-3">
          <Keyboard className="h-4 w-4 text-accent" />
          <h2 className="text-[14px] font-semibold tracking-tight text-fg-primary">
            Keyboard shortcuts
          </h2>
          <Badge tone="accent">global</Badge>
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="ml-auto grid h-6 w-6 place-items-center rounded text-fg-secondary hover:bg-surface-2 hover:text-fg-primary"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </header>

        <div className="grid grid-cols-1 gap-3 p-4 md:grid-cols-2">
          {grouped.map((g) => (
            <section key={g.scope}>
              <h3 className="mb-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-fg-muted">
                {SCOPE_LABEL[g.scope]}
              </h3>
              <ul className="space-y-0.5 rounded border border-border-subtle bg-surface-2/40 p-1.5">
                {g.items.map((s) => {
                  const Icon = s.icon;
                  return (
                    <li
                      key={s.label}
                      className="flex items-center gap-2 rounded px-1.5 py-1 text-[11px] hover:bg-surface-2/70"
                    >
                      <Icon className="h-3 w-3 flex-none text-fg-muted" />
                      <span className="min-w-0 flex-1 text-fg-secondary">{s.label}</span>
                      <span className="flex items-center gap-0.5">
                        {s.keys.map((k, i) => (
                          <kbd
                            key={i}
                            className="inline-block rounded border border-border-default bg-surface-3 px-1.5 py-0.5 font-mono text-[10px] text-fg-primary"
                          >
                            {k}
                          </kbd>
                        ))}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
        </div>

        <footer className="flex items-center justify-between border-t border-border-default bg-surface-2 px-4 py-2 font-mono text-[10.5px] text-fg-muted">
          <span>
            Hit <kbd className="rounded border border-border-default bg-surface-3 px-1 py-0.5">?</kbd> any time to toggle this.
          </span>
          <span>Customisable bindings — planned.</span>
        </footer>
      </div>
    </>
  );
}
